import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSLogo,
  MDSLogoKind,
  MDSLogoSize,
} from "@/mds";
import {
  DEFAULT_QUICK_THOUGHTS_TRIAL_PITCH_TIMINGS,
  type QuickThoughtsTrialPitchTimings,
} from "./quick-thoughts-trial-pitch-timings";

const SPOKEN_WORDS = "I need to find a new dentist sometime soon.".split(" ");
const INITIAL_BAR_HEIGHTS = [6, 11, 8, 13, 7];
const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

interface QuickThoughtsAnimationFrame {
  acknowledgementVisible: boolean;
  barHeights: number[];
  captureVisible: boolean;
  divider1Visible: boolean;
  divider2Visible: boolean;
  initialUserMessageVisible: boolean;
  message1Visible: boolean;
  message2Visible: boolean;
  message3Visible: boolean;
  receded: boolean;
  recording: boolean;
  released: boolean;
  sheetVisible: boolean;
  transcriptVisible: boolean;
  visibleWordCount: number;
}

const buildInitialFrame = (): QuickThoughtsAnimationFrame => ({
  acknowledgementVisible: false,
  barHeights: INITIAL_BAR_HEIGHTS,
  captureVisible: false,
  divider1Visible: false,
  divider2Visible: false,
  initialUserMessageVisible: false,
  message1Visible: false,
  message2Visible: false,
  message3Visible: false,
  receded: false,
  recording: false,
  released: false,
  sheetVisible: false,
  transcriptVisible: false,
  visibleWordCount: 0,
});

const buildEndFrame = (): QuickThoughtsAnimationFrame => ({
  acknowledgementVisible: true,
  barHeights: INITIAL_BAR_HEIGHTS,
  captureVisible: true,
  divider1Visible: true,
  divider2Visible: true,
  initialUserMessageVisible: true,
  message1Visible: true,
  message2Visible: true,
  message3Visible: true,
  receded: true,
  recording: false,
  released: true,
  sheetVisible: true,
  transcriptVisible: true,
  visibleWordCount: SPOKEN_WORDS.length,
});

const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
};

export type QuickThoughtsTrialAnimationMode = "play" | "end-state";
export type QuickThoughtsCaptureMethod = "typed" | "voice";

interface QuickThoughtsFollowThroughAnimationProps {
  captureMethod?: QuickThoughtsCaptureMethod;
  mode?: QuickThoughtsTrialAnimationMode;
  showReplay?: boolean;
  timings?: QuickThoughtsTrialPitchTimings;
}

/** Demonstrates Mem negotiating a follow-up until a loose thought is handled. */
export const QuickThoughtsFollowThroughAnimation = ({
  captureMethod = "voice",
  mode = "play",
  showReplay = true,
  timings = DEFAULT_QUICK_THOUGHTS_TRIAL_PITCH_TIMINGS,
}: QuickThoughtsFollowThroughAnimationProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldRenderEndState = mode === "end-state" || prefersReducedMotion;
  const [runId, setRunId] = useState(0);
  const [transitionsAreEnabled, setTransitionsAreEnabled] = useState(true);
  const replayFrameRef = useRef<number>();
  const [frame, setFrame] = useState<QuickThoughtsAnimationFrame>(() =>
    shouldRenderEndState ? buildEndFrame() : buildInitialFrame()
  );

  useEffect(() => {
    return () => {
      if (replayFrameRef.current !== undefined) {
        window.cancelAnimationFrame(replayFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (shouldRenderEndState) {
      setFrame(buildEndFrame());
      return;
    }

    const scheduledTimeouts: number[] = [];
    let transcriptInterval: number | undefined;
    const schedule = (callback: () => void, delayMs: number) => {
      scheduledTimeouts.push(window.setTimeout(callback, delayMs));
    };

    setFrame(buildInitialFrame());
    if (captureMethod === "typed") {
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, sheetVisible: true }));
      }, timings.typedSheetInMs);
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, initialUserMessageVisible: true }));
      }, timings.typedUserMessageInMs);
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, acknowledgementVisible: true }));
      }, timings.typedAcknowledgementInMs);
    } else {
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, captureVisible: true }));
      }, timings.captureInMs);
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, recording: true }));
      }, timings.recordingInMs);
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, transcriptVisible: true }));
        transcriptInterval = window.setInterval(() => {
          setFrame(currentFrame => ({
            ...currentFrame,
            visibleWordCount: Math.min(currentFrame.visibleWordCount + 1, SPOKEN_WORDS.length),
          }));
        }, timings.wordMs);
      }, timings.transcriptInMs);
      schedule(() => {
        if (transcriptInterval !== undefined) {
          window.clearInterval(transcriptInterval);
          transcriptInterval = undefined;
        }

        setFrame(currentFrame => ({
          ...currentFrame,
          recording: false,
          released: true,
          visibleWordCount: SPOKEN_WORDS.length,
        }));
      }, timings.releaseInMs);
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, receded: true }));
      }, timings.recedeInMs);
      schedule(() => {
        setFrame(currentFrame => ({ ...currentFrame, sheetVisible: true }));
      }, timings.sheetInMs);
    }
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, divider1Visible: true }));
    }, timings.divider1InMs);
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, message1Visible: true }));
    }, timings.message1InMs);
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, message2Visible: true }));
    }, timings.message2InMs);
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, divider2Visible: true }));
    }, timings.divider2InMs);
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, message3Visible: true }));
    }, timings.message3InMs);

    return () => {
      for (const timeout of scheduledTimeouts) {
        window.clearTimeout(timeout);
      }

      if (transcriptInterval !== undefined) {
        window.clearInterval(transcriptInterval);
      }
    };
  }, [captureMethod, runId, shouldRenderEndState, timings]);

  useEffect(() => {
    if (!frame.recording || shouldRenderEndState) {
      return;
    }

    const barInterval = window.setInterval(() => {
      setFrame(currentFrame => ({
        ...currentFrame,
        barHeights: currentFrame.barHeights.map(() => {
          const isQuietFrame = Math.random() < timings.barQuietProbability;
          const minimumHeight = isQuietFrame ? 3 : 5;
          const heightRange = isQuietFrame ? 3 : 12;

          return Math.round(minimumHeight + Math.random() * heightRange);
        }),
      }));
    }, timings.barTickMs);

    return () => window.clearInterval(barInterval);
  }, [frame.recording, shouldRenderEndState, timings]);

  const resolveTransitionMs = (durationMs: number): number => {
    if (shouldRenderEndState || !transitionsAreEnabled) {
      return 0;
    }

    return durationMs;
  };

  const replay = () => {
    setTransitionsAreEnabled(false);
    setFrame(buildInitialFrame());

    replayFrameRef.current = window.requestAnimationFrame(() => {
      setTransitionsAreEnabled(true);
      setRunId(currentRunId => currentRunId + 1);
      replayFrameRef.current = undefined;
    });
  };

  return (
    <AnimationColumn data-animation-column>
      {!shouldRenderEndState && showReplay ? (
        <ReplayButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.ContrastAccent}
          onPress={replay}
        >
          <MDSIcon aria-hidden="true" kind={MDSIconKind.Refresh} size={12} />
          Replay
        </ReplayButton>
      ) : null}

      <Stage aria-label={`Mem Agent ${captureMethod} quick-thought follow-through example`}>
        {captureMethod === "voice" ? (
          <CaptureGroup
            $isReceded={frame.receded}
            $isVisible={frame.captureVisible}
            $transitionMs={resolveTransitionMs(timings.captureTransitionMs)}
          >
            <CaptureCircleWrap>
              <Spinner
                aria-hidden="true"
                viewBox="0 0 52 52"
                $isAnimated={frame.recording && !shouldRenderEndState}
                $isVisible={frame.recording}
                $revolutionMs={timings.spinnerRevolutionMs}
              >
                <circle
                  cx="26"
                  cy="26"
                  r="23"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray="40 105"
                />
              </Spinner>
              <ReleaseRing
                aria-hidden="true"
                $isReleased={frame.released}
                $transitionMs={resolveTransitionMs(timings.releaseTransitionMs)}
              />
              <CaptureCore
                $isRecording={frame.recording || frame.released}
                $transitionMs={resolveTransitionMs(timings.coreTransitionMs)}
              >
                {frame.recording ? (
                  <VoiceBars aria-hidden="true">
                    {frame.barHeights.map((height, index) => (
                      <VoiceBar key={index} $height={height} $transitionMs={timings.barTickMs} />
                    ))}
                  </VoiceBars>
                ) : frame.released ? (
                  <Checkmark aria-hidden="true" viewBox="0 0 24 24">
                    <CheckPath
                      points="5,13 10,18 19,7"
                      $isDrawn={frame.released}
                      $transitionMs={resolveTransitionMs(timings.releaseTransitionMs)}
                    />
                  </Checkmark>
                ) : (
                  <CaptureLogo aria-hidden="true" kind={MDSLogoKind.ArrowsCropped} />
                )}
              </CaptureCore>
            </CaptureCircleWrap>

            <Transcript
              $isVisible={frame.transcriptVisible}
              $transitionMs={resolveTransitionMs(timings.transcriptFadeMs)}
            >
              {SPOKEN_WORDS.slice(0, frame.visibleWordCount).join(" ")}
            </Transcript>
          </CaptureGroup>
        ) : null}

        <ChatCard
          $isTyped={captureMethod === "typed"}
          $isVisible={frame.sheetVisible}
          $transitionMs={resolveTransitionMs(timings.sheetCardTransitionMs)}
        >
          <ChatHeader>
            <ChatHeaderLogo aria-hidden="true" kind={MDSLogoKind.ArrowsCropped} />
            <ChatName>Mem</ChatName>
          </ChatHeader>

          <Conversation aria-live="polite">
            {captureMethod === "typed" ? (
              <>
                <TypedUserMessage
                  $isVisible={frame.initialUserMessageVisible}
                  $transitionMs={resolveTransitionMs(timings.messageTransitionMs)}
                >
                  I need to find a new dentist soon.
                </TypedUserMessage>

                <AcknowledgementMessage
                  $isVisible={frame.acknowledgementVisible}
                  $transitionMs={resolveTransitionMs(timings.messageTransitionMs)}
                >
                  <MessageAvatar aria-hidden="true">
                    <MDSLogo kind={MDSLogoKind.LogoCircle} size={MDSLogoSize.Small} />
                  </MessageAvatar>
                  <AgentMessageText>
                    Got it—I&apos;ll check back in a few days about finding a dentist. 🦷
                  </AgentMessageText>
                </AcknowledgementMessage>
              </>
            ) : null}

            <Divider
              $isVisible={frame.divider1Visible}
              $transitionMs={resolveTransitionMs(timings.dividerFadeMs)}
            >
              <DividerLine />
              <span>A few days later</span>
              <DividerLine />
            </Divider>

            <AgentMessage
              $isVisible={frame.message1Visible}
              $transitionMs={resolveTransitionMs(timings.messageTransitionMs)}
            >
              <MessageAvatar aria-hidden="true">
                <MDSLogo kind={MDSLogoKind.LogoCircle} size={MDSLogoSize.Small} />
              </MessageAvatar>
              <AgentMessageText>Did you get a chance to look for a dentist?</AgentMessageText>
            </AgentMessage>

            <UserMessage
              $isVisible={frame.message2Visible}
              $transitionMs={resolveTransitionMs(timings.messageTransitionMs)}
            >
              Not yet
            </UserMessage>

            <Divider
              $isVisible={frame.divider2Visible}
              $transitionMs={resolveTransitionMs(timings.dividerFadeMs)}
            >
              <DividerLine />
              <span>The next week</span>
              <DividerLine />
            </Divider>

            <AgentMessage
              $isVisible={frame.message3Visible}
              $transitionMs={resolveTransitionMs(timings.messageTransitionMs)}
            >
              <MessageAvatar aria-hidden="true">
                <MDSLogo kind={MDSLogoKind.LogoCircle} size={MDSLogoSize.Small} />
              </MessageAvatar>
              <AgentMessageText>
                You still wanted to find a dentist. Want me to help you make a shortlist?
              </AgentMessageText>
            </AgentMessage>
          </Conversation>
        </ChatCard>
      </Stage>
    </AnimationColumn>
  );
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const releasePulse = keyframes`
  0% { opacity: .8; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.7); }
`;

const AnimationColumn = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 760px) {
    width: 100%;
    max-width: 400px;
    align-self: center;
  }
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  height: 460px;
  transform: translateY(-20px);

  @media (max-width: 760px) {
    width: 125%;
    height: 320px;
    transform: translateY(-48px) scale(0.8);
    transform-origin: top center;
  }
`;

const CaptureGroup = styled.div<{
  $isReceded: boolean;
  $isVisible: boolean;
  $transitionMs: number;
}>`
  position: absolute;
  z-index: 0;
  top: 206px;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  transform: ${({ $isReceded, $isVisible }) => {
    if ($isReceded) return "translateY(-192px) scale(.9)";
    return $isVisible ? "translateY(0)" : "translateY(10px)";
  }};
  transform-origin: top center;
  opacity: ${({ $isReceded, $isVisible }) => {
    if ($isReceded) return 0.5;
    return $isVisible ? 1 : 0;
  }};
  transition:
    opacity ${({ $transitionMs }) => $transitionMs}ms ease,
    transform ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const CaptureCircleWrap = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  flex: none;
`;

const Spinner = styled.svg<{
  $isAnimated: boolean;
  $isVisible: boolean;
  $revolutionMs: number;
}>`
  position: absolute;
  inset: -6px;
  width: 60px;
  height: 60px;
  color: ${({ theme }) => theme.colors.icon.emphasis};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  animation: ${({ $isAnimated, $revolutionMs }) =>
    $isAnimated
      ? css`
          ${spin} ${$revolutionMs}ms linear infinite
        `
      : "none"};
`;

const ReleaseRing = styled.span<{ $isReleased: boolean; $transitionMs: number }>`
  position: absolute;
  inset: -3px;
  display: block;
  border: 2.5px solid ${({ theme }) => theme.colors.icon.emphasis};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  opacity: 0;
  animation: ${({ $isReleased, $transitionMs }) =>
    $isReleased
      ? css`
          ${releasePulse} ${$transitionMs}ms ease-out both
        `
      : "none"};
`;

const CaptureCore = styled.div<{ $isRecording: boolean; $transitionMs: number }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: ${({ $isRecording, theme }) =>
    $isRecording
      ? theme.colors.background.button.emphasis.default
      : theme.colors.background.button.brand.default};
  transition: background ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const CaptureLogo = styled(MDSLogo)`
  width: 24px;
  height: 14px;

  path {
    fill: white;
  }
`;

const VoiceBars = styled.div`
  display: flex;
  height: 18px;
  align-items: center;
  gap: 2px;
`;

const VoiceBar = styled.span<{ $height: number; $transitionMs: number }>`
  width: 3px;
  height: ${({ $height }) => $height}px;
  border-radius: 2px;
  background: white;
  transition: height ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const Checkmark = styled.svg`
  width: 22px;
  height: 22px;
`;

const CheckPath = styled.polyline<{ $isDrawn: boolean; $transitionMs: number }>`
  fill: none;
  stroke: white;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 24;
  stroke-dashoffset: ${({ $isDrawn }) => ($isDrawn ? 0 : 24)};
  transition: stroke-dashoffset ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const Transcript = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  min-height: 22px;
  flex: 1;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.modal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  font-style: italic;
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const ChatCard = styled.div<{
  $isTyped: boolean;
  $isVisible: boolean;
  $transitionMs: number;
}>`
  position: absolute;
  z-index: 1;
  top: ${({ $isTyped }) => ($isTyped ? 0 : 66)}px;
  right: 0;
  left: 0;
  min-height: 348px;
  overflow: hidden;
  transform: ${({ $isVisible }) => ($isVisible ? "translateY(0)" : "translateY(96px)")};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.chat};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
  transition: transform ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]} ${theme.spacing[3]}`};
`;

const ChatHeaderLogo = styled(MDSLogo)`
  height: 9px;
`;

const ChatName = styled.span`
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: ${({ theme }) => theme.fontSizes["text-m"]};
  font-weight: ${({ theme }) => theme.fontWeights.smedium};
  line-height: ${({ theme }) => theme.lineHeights["text-m"]};
`;

const Conversation = styled.div`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]} ${theme.spacing[5]}`};
`;

const Divider = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin: ${({ theme }) => `${theme.spacing[3]} 0`};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const DividerLine = styled.span`
  height: 1px;
  flex: 1;
  background: ${({ theme }) => theme.colors.text.tertiary};
  opacity: 0.45;
`;

const AgentMessage = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  display: flex;
  max-width: 94%;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  transform: ${({ $isVisible }) => ($isVisible ? "translateY(0)" : "translateY(8px)")};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity ${({ $transitionMs }) => $transitionMs}ms ease,
    transform ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const MessageAvatar = styled.span`
  display: flex;
  width: 24px;
  height: 24px;
  flex: none;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.max};
`;

const AgentMessageText = styled.span`
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
`;

const UserMessage = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  width: fit-content;
  max-width: 82%;
  margin-top: ${({ theme }) => theme.spacing[3]};
  margin-left: auto;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  transform: ${({ $isVisible }) => ($isVisible ? "translateY(0)" : "translateY(8px)")};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.large} ${theme.borderRadius.small} ${theme.borderRadius.large} ${theme.borderRadius.large}`};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity ${({ $transitionMs }) => $transitionMs}ms ease,
    transform ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const TypedUserMessage = styled(UserMessage)`
  margin-top: 0;
`;

const AcknowledgementMessage = styled(AgentMessage)`
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const ReplayButton = styled(MDSButton)`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  min-height: 28px;
  transform: translateY(-4px);
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 160ms ease,
    transform 160ms ease;

  .mds-icon {
    color: ${({ theme }) => theme.colors.icon.secondary};
  }

  [data-animation-column]:hover &,
  [data-animation-column]:focus-within &,
  &:focus-visible {
    transform: translateY(0);
    opacity: 0.82;
    pointer-events: auto;
  }

  &:hover,
  &[data-hovered]:not([data-disabled]) {
    opacity: 1;
  }
`;
